'use client';

import { useMemo, useState, FC } from 'react';
import { observer } from 'mobx-react-lite';

import LocaleListVM from '@/react/view-models/LocaleListVM';
import { useLocale } from '@/react/hooks/useLocale';
import { Icon, IconSizeProp, IconTypes } from '@/react/components/core-ui/Icon';
import Button from '@/react/components/core-ui/Button';
import { getFlagByCountry } from '@/react/components/core-ui/Icon/getFlagByCountry';
import { Link } from '@/react/components/core-ui/Link';
import { getCountryName } from '@/services/isomorphic/I18NService';
import { createClickOutsideRef } from '@/react/utils/ref-utils/createClickOutsideRef';
import { CountryLink } from '../CountryLink';
import { ViewMoreCountries } from '../ViewMoreCountries';

import S from './styles.base.module.scss';

/**
 * CountrySelector component.
 *
 * Renders a country selector popover, typically used in the footer.
 * Includes:
 *   - A country popover (for choosing region/country).
 */
export const CountrySelector: FC = observer(function CountrySelector() {
  /** Track open/closed state for the country popover. */
  const [isCountryOpen, setIsCountryOpen] = useState(false);

  /** ViewModel for available sites/countries. */
  const vm = useMemo(() => new LocaleListVM(), []);

  /** Current locale info (e.g., "US"). */
  const locale = useLocale();
  const flagIcon = getFlagByCountry(locale.country);

  /** Get the display name for the current country. */
  const currentCountryName = getCountryName(locale.country);

  /** Toggle handler for country popover. */
  const toggleCountry = (): void => {
    setIsCountryOpen((prev) => !prev);
  };

  /**
   * Allows clicking outside the component to close the country popover.
   */
  const closeOnOutsideClick = useMemo(
    () =>
      createClickOutsideRef<HTMLDivElement>(() => {
        setIsCountryOpen(false);
      }),
    [isCountryOpen]
  );

  return (
    <div className={S.countrySelectorButton} ref={closeOnOutsideClick}>
      {/* ================= COUNTRY SELECTOR ================= */}
      <Button className={S.button} variant="text" onClick={toggleCountry}>
        <Icon
          className={S.flagIcon}
          icon={flagIcon}
          size={IconSizeProp.SizeLG}
        />
        {currentCountryName}
        <Icon
          className={S.arrowIcon}
          icon={IconTypes.SortUp}
          size={IconSizeProp.SizeXS}
        />
      </Button>

      {/* Country popover with available sites */}
      {isCountryOpen && (
        <div className={S.popoverContainer}>
          {vm.filteredSites.map((item) => (
            <div className={S.link} key={item.id}>
              <CountryLink link={item.url} name={item.name} flag={item.icon} />
            </div>
          ))}
          <div className={S.viewMore}>
            <Link
              className={S.viewMoreCountries}
              href="/choose-country"
              variant="underline"
            >
              <ViewMoreCountries />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
});
